import React from 'react';
import { useTheme } from '../lib/theme-provider';

interface ThemedButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

export function ThemedButton({ 
  variant = 'primary', 
  size = 'md',
  children,
  className = '',
  disabled,
  ...props 
}: ThemedButtonProps) {
  const { theme } = useTheme();
  
  const getColors = () => {
    if (disabled) {
      return {
        bg: theme.colors['text-muted'],
        text: theme.colors['text-secondary'],
      };
    }
    
    switch (variant) {
      case 'primary':
        return {
          bg: theme.colors['accent-gold'],
          text: theme.colors['bg-primary'],
        };
      case 'secondary':
        return {
          bg: theme.colors['accent-purple'],
          text: theme.colors['text-primary'],
        };
      case 'danger':
        return {
          bg: theme.colors['accent-red'],
          text: theme.colors['text-primary'],
        };
      default:
        return {
          bg: theme.colors['accent-gold'],
          text: theme.colors['bg-primary'],
        };
    }
  };
  
  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm';
      case 'md':
        return 'px-4 py-2 text-base';
      case 'lg':
        return 'px-6 py-3 text-lg';
      default:
        return 'px-4 py-2 text-base';
    }
  };
  
  const colors = getColors();
  
  return (
    <button
      className={`
        transition-theme hover-scale-theme
        font-semibold rounded-lg
        active:scale-95
        disabled:opacity-50 disabled:cursor-not-allowed
        ${getSizeStyles()}
        ${className}
      `}
      style={{
        backgroundColor: colors.bg,
        color: colors.text,
        boxShadow: !disabled ? `0 4px 15px rgba(255, 215, 0, 0.3)` : undefined,
      }}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}
